import { getNormalizedDate } from "pages/schedule/schedule-page/compare-schedule-2/TemporalLayout";
import { DateUtils } from "utils/dateutils";

export interface TimelineBreak {
  x: number; // timeline x where the zigzag sits
  days: number; // real days the timeline leaps over at this break
  side: "left" | "right"; // which side of the design span this break sits on
}

export interface OutsideZone {
  start: number; // timeline x where the compressed out-of-scope zone begins
  end: number; // timeline x where it ends
  side: "left" | "right";
}

export interface Gridline {
  x: number; // timeline x of a calendar boundary inside the to-scale region
  label: string; // e.g. "Feb" or "Jan '26"
}

export interface DateScale {
  // Maps a date to a timeline x position, or null for a null date.
  x: (date: string | null) => number | null;
  // Inverse of `x`: maps a timeline x (same coordinate space `x(date)` returns,
  // i.e. BEFORE any labelColumn offset) back to an ISO "YYYY-MM-DD" date. The
  // end MARGINs extrapolate at the main region's px-per-day rate so a drag can
  // reach dates beyond the current first/last — the scale refits once the new
  // date persists. Break gaps still clamp to the domain end they sit against.
  // null only for a degenerate scale.
  xToDate: (x: number) => string | null;
  // Breaks where dates outside the design span are reached via a time-jump.
  breaks: TimelineBreak[];
  // x-span of the in-scope (design-milestone) region, drawn to scale.
  inScope: { start: number; end: number };
  // Compressed regions holding dates outside the design span (drawn faded).
  outsideZones: OutsideZone[];
  // Month (or quarter) markers inside the in-scope region, for the time scale.
  gridlines: Gridline[];
  // Weekly tick x's — populated only for short spans (< ~6 months) so the
  // ruler reads as a proper finer scale; empty otherwise.
  weekTicks: number[];
  // Intrinsic timeline width.
  contentWidth: number;
}

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const MARGIN = 80; // gap before the first / after the last date

export interface DateScaleOptions {
  /** Drop dates outside the design span so the ruler has no compressed zones. */
  designSpanOnly?: boolean;
  /** Blank px before the first and after the last in-scope date. */
  sideMargin?: number;
}

/** Card width (256) plus the 16px pin gap, plus a little air before the column line. */
export const PLANNER_SIDE_MARGIN = 256 + 16 + 12;
// The design-milestone span maps to this many px, to scale (gaps proportional
// to real durations). Bounded so the timeline doesn't grow huge / scroll a lot.
const MAIN_W = 900;
// Compressed zone for dates that fall outside the design span (a time-jump
// separates it from the in-range timeline).
const OUTSIDE_W = 90;
const BREAK_GAP = 34; // px reserved for the zigzag at a time-jump
const MS_PER_DAY = 86_400_000;

// "YYYYMMDD" → integer day number (deterministic, timezone-free).
function dayNumber(key: string): number {
  const year = Number(key.slice(0, 4));
  const month = Number(key.slice(4, 6)) - 1;
  const day = Number(key.slice(6, 8));
  return Math.floor(Date.UTC(year, month, day) / MS_PER_DAY);
}

type ScaleBands = {
  dpMin: number;
  dpMax: number;
  span: number;
  leftStart: number;
  leftW: number;
  leftMin: number;
  leftMax: number;
  leftSpan: number;
  mainStart: number;
  rightStart: number;
  rightW: number;
  rightMin: number;
  rightMax: number;
  rightSpan: number;
  hasLeft: boolean;
  hasRight: boolean;
  contentWidth: number;
};

function computeBands(
  allDays: number[],
  dpMin: number,
  dpMax: number,
  margin: number = MARGIN
): ScaleBands {
  const leftDays = allDays.filter((d) => d < dpMin);
  const rightDays = allDays.filter((d) => d > dpMax);
  const hasLeft = leftDays.length > 0;
  const hasRight = rightDays.length > 0;

  const leftStart = margin;
  const leftW = hasLeft ? OUTSIDE_W : 0;
  const mainStart = leftStart + leftW + (hasLeft ? BREAK_GAP : 0);
  const rightStart = mainStart + MAIN_W + (hasRight ? BREAK_GAP : 0);
  const rightW = hasRight ? OUTSIDE_W : 0;

  const leftMin = hasLeft ? Math.min(...leftDays) : 0;
  const leftMax = hasLeft ? Math.max(...leftDays) : 0;
  const rightMin = hasRight ? Math.min(...rightDays) : 0;
  const rightMax = hasRight ? Math.max(...rightDays) : 0;

  return {
    dpMin,
    dpMax,
    span: Math.max(dpMax - dpMin, 1),
    leftStart,
    leftW,
    leftMin,
    leftMax,
    leftSpan: Math.max(leftMax - leftMin, 1),
    mainStart,
    rightStart,
    rightW,
    rightMin,
    rightMax,
    rightSpan: Math.max(rightMax - rightMin, 1),
    hasLeft,
    hasRight,
    contentWidth: rightStart + rightW + margin
  };
}

function xForDayIn(b: ScaleBands, dn: number): number {
  if (dn < b.dpMin)
    return b.leftStart + ((dn - b.leftMin) / b.leftSpan) * b.leftW;
  if (dn > b.dpMax)
    return b.rightStart + ((dn - b.rightMin) / b.rightSpan) * b.rightW;
  return b.mainStart + ((dn - b.dpMin) / b.span) * MAIN_W;
}

function monthLabel(month: number, year: number): string {
  return month === 0
    ? `${MONTH_ABBR[month]} '${String(year).slice(2)}`
    : MONTH_ABBR[month];
}

function gridStep(dpMin: number, dpMax: number): number {
  return (dpMax - dpMin) / 30.44 > 18 ? 3 : 1;
}

// Month markers inside the to-scale region. Step to quarters for long spans
// so the axis doesn't get crowded.
function computeInScopeMonthGridlines(
  dpMin: number,
  dpMax: number,
  xForDay: (dn: number) => number
): Gridline[] {
  const gridlines: Gridline[] = [];
  const step = gridStep(dpMin, dpMax);
  const firstDate = new Date(dpMin * MS_PER_DAY);
  let gy = firstDate.getUTCFullYear();
  let gm = firstDate.getUTCMonth(); // 0-based
  let gdn = Math.floor(Date.UTC(gy, gm, 1) / MS_PER_DAY);
  while (gdn < dpMin) {
    gm += 1;
    if (gm > 11) {
      gm = 0;
      gy += 1;
    }
    gdn = Math.floor(Date.UTC(gy, gm, 1) / MS_PER_DAY);
  }
  while (gdn <= dpMax) {
    gridlines.push({
      x: xForDay(gdn),
      label: monthLabel(gm, gy)
    });
    gm += step;
    while (gm > 11) {
      gm -= 12;
      gy += 1;
    }
    gdn = Math.floor(Date.UTC(gy, gm, 1) / MS_PER_DAY);
  }
  return gridlines;
}

// The scale continues through the end MARGINs at the main px-per-day rate —
// the same rate xToDate extrapolates — so the ruler shows the whole surface a
// milestone drag can reach, not just the first→last milestone span. Skipped
// across a compressed outside-zone side only for gridline correctness: the
// margin there extends from the zone's far end.
function appendMarginGridlines(
  gridlines: Gridline[],
  b: ScaleBands
): Gridline[] {
  const step = gridStep(b.dpMin, b.dpMax);
  const pxPerDayRate = MAIN_W / b.span;
  const firstDrawnDay = b.hasLeft ? b.leftMin : b.dpMin;
  const firstDrawnX = b.hasLeft ? b.leftStart : b.mainStart;
  const lastDrawnDay = b.hasRight ? b.rightMax : b.dpMax;
  const lastDrawnX = b.hasRight
    ? b.rightStart + b.rightW
    : b.mainStart + MAIN_W;

  // Trailing margin: month boundaries after the last drawn day, while on canvas.
  {
    const d = new Date(lastDrawnDay * MS_PER_DAY);
    let my = d.getUTCFullYear();
    let mm = d.getUTCMonth() + 1; // first boundary strictly after lastDrawnDay
    while (mm > 11) {
      mm -= 12;
      my += 1;
    }
    let mdn = Math.floor(Date.UTC(my, mm, 1) / MS_PER_DAY);
    while (lastDrawnX + (mdn - lastDrawnDay) * pxPerDayRate <= b.contentWidth) {
      gridlines.push({
        x: lastDrawnX + (mdn - lastDrawnDay) * pxPerDayRate,
        label: monthLabel(mm, my)
      });
      mm += step;
      while (mm > 11) {
        mm -= 12;
        my += 1;
      }
      mdn = Math.floor(Date.UTC(my, mm, 1) / MS_PER_DAY);
    }
  }
  // Leading margin: month boundaries strictly before the first drawn day.
  {
    const d = new Date(firstDrawnDay * MS_PER_DAY);
    let my = d.getUTCFullYear();
    let mm = d.getUTCMonth(); // boundary at or before firstDrawnDay
    let mdn = Math.floor(Date.UTC(my, mm, 1) / MS_PER_DAY);
    while (firstDrawnX - (firstDrawnDay - mdn) * pxPerDayRate >= 0) {
      if (mdn < firstDrawnDay) {
        gridlines.push({
          x: firstDrawnX - (firstDrawnDay - mdn) * pxPerDayRate,
          label: monthLabel(mm, my)
        });
      }
      mm -= step;
      while (mm < 0) {
        mm += 12;
        my -= 1;
      }
      mdn = Math.floor(Date.UTC(my, mm, 1) / MS_PER_DAY);
    }
  }
  // The renderer derives mid-month ticks from CONSECUTIVE entries, so the
  // margin additions must be merged in x-order, not appended.
  gridlines.sort((a, c) => a.x - c.x);
  return gridlines;
}

function computeMonthGridlines(
  b: ScaleBands,
  xForDay: (dn: number) => number
): Gridline[] {
  return appendMarginGridlines(
    computeInScopeMonthGridlines(b.dpMin, b.dpMax, xForDay),
    b
  );
}

// Week ticks for short spans (≤ ~6 months) — a finer "proper" scale. These
// run through the margins too (same continuation as the month boundaries).
function computeWeekTicks(
  b: ScaleBands,
  xForDay: (dn: number) => number,
  inSpanOnly = false
): number[] {
  if (b.dpMax - b.dpMin > 183) return [];
  const weekTicks: number[] = [];
  const pxPerDayRate = MAIN_W / b.span;
  for (let dn = b.dpMin; dn <= b.dpMax + 0.5; dn += 7)
    weekTicks.push(xForDay(dn));
  if (inSpanOnly) return weekTicks;
  if (!b.hasRight) {
    for (
      let dn = b.dpMax + 7;
      b.mainStart + MAIN_W + (dn - b.dpMax) * pxPerDayRate <= b.contentWidth;
      dn += 7
    )
      weekTicks.push(b.mainStart + MAIN_W + (dn - b.dpMax) * pxPerDayRate);
  }
  if (!b.hasLeft) {
    for (
      let dn = b.dpMin - 7;
      b.mainStart - (b.dpMin - dn) * pxPerDayRate >= 0;
      dn -= 7
    )
      weekTicks.push(b.mainStart - (b.dpMin - dn) * pxPerDayRate);
  }
  return weekTicks;
}

function computeBreakMarkers(b: ScaleBands): {
  breaks: TimelineBreak[];
  outsideZones: OutsideZone[];
} {
  const breaks: TimelineBreak[] = [];
  const outsideZones: OutsideZone[] = [];
  if (b.hasLeft) {
    breaks.push({
      x: b.mainStart - BREAK_GAP / 2,
      days: b.dpMin - b.leftMax,
      side: "left"
    });
    outsideZones.push({
      start: b.leftStart,
      end: b.leftStart + b.leftW,
      side: "left"
    });
  }
  if (b.hasRight) {
    breaks.push({
      x: b.rightStart - BREAK_GAP / 2,
      days: b.rightMin - b.dpMax,
      side: "right"
    });
    outsideZones.push({
      start: b.rightStart,
      end: b.rightStart + b.rightW,
      side: "right"
    });
  }
  return { breaks, outsideZones };
}

function dayToDate(dn: number): string {
  const d = new Date(Math.round(dn) * MS_PER_DAY); // dn is a UTC epoch-day index
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Inverse of xForDay — same 3 regions (left outside-zone / to-scale main /
// right outside-zone), each inverted from its own linear mapping. Falling
// outside all three means the cursor is over a margin or the zigzag gap, so
// we clamp to whichever domain end that gap sits next to.
// The end margins extrapolate at the main region's px-per-day rate, so a
// milestone drag can reach dates beyond the current first/last date (the
// scale refits around the new extremum once the write lands). Interior
// gaps (the zigzag time-jumps) still clamp to the domain end they abut —
// they compress real days, so no single linear rate is honest there.
function dayOnMainBand(b: ScaleBands, x: number): number | null {
  if (x >= b.mainStart && x <= b.mainStart + MAIN_W) {
    return b.dpMin + ((x - b.mainStart) / MAIN_W) * b.span;
  }
  return null;
}

function dayOnLeftBand(b: ScaleBands, x: number): number | null {
  if (
    b.hasLeft &&
    x >= b.leftStart &&
    x <= b.leftStart + b.leftW &&
    b.leftW > 0
  ) {
    return b.leftMin + ((x - b.leftStart) / b.leftW) * b.leftSpan;
  }
  return null;
}

function dayOnRightBand(b: ScaleBands, x: number): number | null {
  if (
    b.hasRight &&
    x >= b.rightStart &&
    x <= b.rightStart + b.rightW &&
    b.rightW > 0
  ) {
    return b.rightMin + ((x - b.rightStart) / b.rightW) * b.rightSpan;
  }
  return null;
}

function dayBeforeScale(
  b: ScaleBands,
  x: number,
  pxPerDay: number
): number | null {
  if (x >= b.leftStart) return null;
  const firstDay = b.hasLeft ? b.leftMin : b.dpMin;
  return firstDay - (b.leftStart - x) / pxPerDay;
}

function dayAfterScale(
  b: ScaleBands,
  x: number,
  pxPerDay: number
): number | null {
  const drawnEnd = b.hasRight ? b.rightStart + b.rightW : b.mainStart + MAIN_W;
  if (x <= drawnEnd) return null;
  const lastDay = b.hasRight ? b.rightMax : b.dpMax;
  return lastDay + (x - drawnEnd) / pxPerDay;
}

function xToDay(b: ScaleBands, x: number): number {
  const pxPerDay = MAIN_W / b.span;
  const onBand =
    dayOnMainBand(b, x) ?? dayOnLeftBand(b, x) ?? dayOnRightBand(b, x);
  if (onBand != null) return onBand;
  const before = dayBeforeScale(b, x, pxPerDay);
  if (before != null) return before;
  const after = dayAfterScale(b, x, pxPerDay);
  if (after != null) return after;
  // interior gaps (zigzag): clamp to the domain end on this side
  if (x < b.mainStart) return b.hasLeft ? b.leftMax : b.dpMin;
  return b.hasRight ? b.rightMin : b.dpMax;
}

// Positions dates to scale within the design-milestone span (gaps proportional
// to elapsed days). Dates before or after that span are pushed into a small
// compressed zone, separated by a zigzag time-jump, so the in-range timeline
// stays visually proportional and the overall width stays bounded.
export function computeDateScale(
  axisDates: string[],
  designDates: string[],
  options?: DateScaleOptions
): DateScale {
  const keyOf = (d: string) => getNormalizedDate(DateUtils.dateTimeObj(d));
  const dayOf = (d: string) => dayNumber(keyOf(d));

  const designDays = Array.from(
    new Set(designDates.filter(Boolean).map(dayOf))
  );
  const allDays = Array.from(new Set(axisDates.filter(Boolean).map(dayOf)));

  // The design milestones define the to-scale range; fall back to all dates
  // when there are fewer than two design milestones.
  const rangeDays = designDays.length >= 2 ? designDays : allDays;
  const dpMin = Math.min(...rangeDays);
  const dpMax = Math.max(...rangeDays);
  // Planner mode keeps the ruler on the design span. Dates outside it are not
  // folded into compressed zones; the lane pins those bid cards in the margins.
  const bandDays = options?.designSpanOnly
    ? allDays.filter((day) => day >= dpMin && day <= dpMax)
    : allDays;
  const bands = computeBands(
    bandDays.length ? bandDays : rangeDays,
    dpMin,
    dpMax,
    options?.sideMargin ?? MARGIN
  );
  const xForDay = (dn: number): number => xForDayIn(bands, dn);

  const { breaks, outsideZones } = computeBreakMarkers(bands);
  const spanOnly = Boolean(options?.designSpanOnly);

  return {
    x: (date: string | null): number | null =>
      date ? xForDay(dayOf(date)) : null,
    xToDate: (x: number): string | null =>
      allDays.length ? dayToDate(xToDay(bands, x)) : null,
    breaks,
    inScope: { start: bands.mainStart, end: bands.mainStart + MAIN_W },
    outsideZones,
    gridlines: spanOnly
      ? computeInScopeMonthGridlines(bands.dpMin, bands.dpMax, xForDay)
      : computeMonthGridlines(bands, xForDay),
    weekTicks: computeWeekTicks(bands, xForDay, spanOnly),
    contentWidth: bands.contentWidth
  };
}
